// src/utils/localStorage.js - localStorage utilities for form persistence

const STORAGE_KEY = 'orald_form_data';
const STORAGE_VERSION = '1.0';
const MAX_STORAGE_SIZE = 50 * 1024 * 1024; // 50MB limit for localStorage

/**
 * Serialize form state for localStorage
 */
export const serializeFormState = (state) => {
  try {
    const serializedState = {
      version: STORAGE_VERSION,
      timestamp: Date.now(),
      patientData: state.patientData,
      reportType: state.reportType,
      selectedUnit: state.selectedUnit,
      selectedDoctor: state.selectedDoctor,
      canalsTraced: state.canalsTraced,
      panoramicViewData: state.panoramicViewData,
      dimensionsData: state.dimensionsData,
      radiologicalImpressions: state.radiologicalImpressions,
      otherImpression: state.otherImpression,
      sinusViewData: state.sinusViewData,
      sagittalViewData: state.sagittalViewData,
      currentTab: state.currentTab,
      // UI States
      panoramicViewUIState: state.panoramicViewUIState,
      canalsUIState: state.canalsUIState,
      dimensionsPdfState: state.dimensionsPdfState,
      regionDetailsPdfState: state.regionDetailsPdfState,
      regionFormStates: state.regionFormStates,
      regionDetailsData: state.regionDetailsData
    };

    return serializedState;
  } catch (error) {
    console.error('Failed to serialize form state:', error);
    throw error;
  }
};

/**
 * Deserialize form state from localStorage
 */
export const deserializeFormState = (serializedData) => {
  try {
    if (!serializedData || serializedData.version !== STORAGE_VERSION) {
      console.warn('Incompatible or missing localStorage data version');
      return null;
    }

    const state = {
      patientData: serializedData.patientData,
      reportType: serializedData.reportType,
      selectedUnit: serializedData.selectedUnit,
      selectedDoctor: serializedData.selectedDoctor,
      canalsTraced: serializedData.canalsTraced || '',
      panoramicViewData: serializedData.panoramicViewData || { panoramicFile1: null, panoramicFile2: null, comments: '' },
      dimensionsData: serializedData.dimensionsData || { pdfFile: null, croppedImage1: null, croppedImage2: null },
      radiologicalImpressions: serializedData.radiologicalImpressions || [],
      otherImpression: serializedData.otherImpression || '',
      sinusViewData: serializedData.sinusViewData || { image: null, selectedImpressions: [], otherImpression: '' },
      sagittalViewData: serializedData.sagittalViewData || { image: null, mainComment: '', mandibleComment: '' },
      currentTab: serializedData.currentTab || 0,
      // UI States
      panoramicViewUIState: serializedData.panoramicViewUIState || { preview1: null, preview2: null, selectedComments: [], otherComments: '' },
      canalsUIState: serializedData.canalsUIState || { selectedCanals: [], otherCanals: '' },
      dimensionsPdfState: serializedData.dimensionsPdfState || { pdfFile: null, convertedImages: [], currentImageIndex: 0, crop: { unit: '%', width: 30, height: 30, x: 25, y: 25 }, completedCrop: null, croppedImageUrl1: null, croppedImageUrl2: null, croppedImageFile1: null, croppedImageFile2: null, loading: false, error: null },
      regionDetailsPdfState: serializedData.regionDetailsPdfState || { pdfFile: null, convertedImages: [], expanded: [], selected: [], loadingPdf: false, error: null },
      regionFormStates: serializedData.regionFormStates || {},
      regionDetailsData: serializedData.regionDetailsData || {}
    };

    return state;
  } catch (error) {
    console.error('Failed to deserialize form state:', error);
    return null;
  }
};

/**
 * Save form state to localStorage
 */
export const saveToLocalStorage = (state) => {
  try {
    console.log('💾 Saving form data to localStorage...');
    const serializedState = serializeFormState(state);
    const dataString = JSON.stringify(serializedState);

    // Check size limit
    if (dataString.length > MAX_STORAGE_SIZE) {
      console.warn('⚠️ Form data exceeds localStorage size limit');
      throw new Error('Form data too large for localStorage');
    }

    localStorage.setItem(STORAGE_KEY, dataString);
    console.log(`✅ Form data saved (${(dataString.length / 1024).toFixed(1)}KB)`);
    return true;
  } catch (error) {
    console.error('❌ Failed to save to localStorage:', error);
    // Try to clear old data and retry once
    try {
      clearLocalStorage();
      const serializedState = serializeFormState(state);
      const dataString = JSON.stringify(serializedState);
      localStorage.setItem(STORAGE_KEY, dataString);
      console.log('✅ Form data saved after clearing old data');
      return true;
    } catch (retryError) {
      console.error('❌ Failed to save even after clearing:', retryError);
      return false;
    }
  }
};

/**
 * Load form state from localStorage
 */
export const loadFromLocalStorage = () => {
  try {
    console.log('📥 Loading form data from localStorage...');
    const dataString = localStorage.getItem(STORAGE_KEY);

    if (!dataString) {
      console.log('ℹ️ No saved form data found');
      return null;
    }

    const serializedData = JSON.parse(dataString);
    const state = deserializeFormState(serializedData);

    if (state) {
      console.log('✅ Form data loaded successfully');
      console.log(`📅 Last saved: ${new Date(serializedData.timestamp).toLocaleString()}`);
    }

    return state;
  } catch (error) {
    console.error('❌ Failed to load from localStorage:', error);
    // Clear corrupted data
    clearLocalStorage();
    return null;
  }
};

/**
 * Clear saved form data from localStorage
 */
export const clearLocalStorage = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    console.log('🗑️ Cleared saved form data');
    return true;
  } catch (error) {
    console.error('❌ Failed to clear localStorage:', error);
    return false;
  }
};

/**
 * Check if saved data exists
 */
export const hasSavedData = () => {
  try {
    const dataString = localStorage.getItem(STORAGE_KEY);
    if (!dataString) return false;

    const data = JSON.parse(dataString);
    return data && data.version === STORAGE_VERSION;
  } catch (error) {
    console.error('Failed to check saved data:', error);
    return false;
  }
};

/**
 * Get saved data info (without loading full data)
 */
export const getSavedDataInfo = () => {
  try {
    const dataString = localStorage.getItem(STORAGE_KEY);
    if (!dataString) return null;

    const data = JSON.parse(dataString);
    return {
      timestamp: data.timestamp,
      version: data.version,
      hasPatientData: !!data.patientData,
      reportType: data.reportType,
      size: `${(dataString.length / 1024).toFixed(1)}KB`
    };
  } catch (error) {
    console.error('Failed to get saved data info:', error);
    return null;
  }
};